import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api } from "../../lib/api";
import { PageTitle } from "./shared";

const Metric = ({ label, value, testid }) => (
  <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.25rem] p-5 text-center" data-testid={testid}>
    <p className="font-serif-display text-3xl text-plum-deep">{value}</p>
    <p className="text-[10px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mt-1">{label}</p>
  </div>
);

const Reports = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/reports").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <p className="text-plum-deep/60">Crunching the numbers…</p>;

  const maxShape = Math.max(1, ...(data.top_shapes || []).map((s) => s.tags));
  const maxColour = Math.max(1, ...(data.top_colours || []).map((c) => c.tags));

  return (
    <div data-testid="console-reports">
      <PageTitle kicker="INSIGHTS" title="REPORTS" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric label="Total revenue (NZD)" value={`$${(data.total_revenue || 0).toFixed(2)}`} testid="report-revenue" />
        <Metric label="Orders" value={data.total_orders || 0} testid="report-orders" />
        <Metric label="Tags printed" value={data.total_tags || 0} testid="report-tags" />
        <Metric label="Avg order value" value={`$${(data.avg_order_value || 0).toFixed(2)}`} testid="report-aov" />
      </div>

      <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6 mt-6" data-testid="report-monthly-chart">
        <h2 className="font-serif-display uppercase text-plum-deep text-xl mb-4">REVENUE BY MONTH</h2>
        {(data.monthly || []).length === 0 ? (
          <p className="text-sm text-plum-deep/60">No orders yet — revenue will chart here.</p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#4A1B4D22" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#4A1B4D" }} />
                <YAxis tick={{ fontSize: 12, fill: "#4A1B4D" }} />
                <Tooltip formatter={(v, name) => (name === "revenue" ? [`$${v}`, "Revenue"] : [v, "Orders"])} />
                <Bar dataKey="revenue" fill="#EC2675" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6" data-testid="report-top-shapes">
          <h2 className="font-serif-display uppercase text-plum-deep text-xl mb-5">MOST POPULAR SHAPES</h2>
          {(data.top_shapes || []).length === 0 ? <p className="text-sm text-plum-deep/60">No data yet.</p> : (
            <ul className="space-y-3">
              {data.top_shapes.map((s) => (
                <li key={s.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{s.name}</span>
                    <span className="text-plum-deep/60">{s.tags} tags</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-cream overflow-hidden">
                    <div className="h-full rounded-full bg-plum" style={{ width: `${(s.tags / maxShape) * 100}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6" data-testid="report-top-colours">
          <h2 className="font-serif-display uppercase text-plum-deep text-xl mb-5">MOST POPULAR COLOURS</h2>
          {(data.top_colours || []).length === 0 ? <p className="text-sm text-plum-deep/60">No data yet.</p> : (
            <ul className="space-y-3">
              {data.top_colours.map((c) => (
                <li key={c.name}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium flex items-center gap-2">
                      <span className="w-3.5 h-3.5 rounded-full inline-block border border-plum-deep/15" style={{ background: c.hex }} />
                      {c.name}
                    </span>
                    <span className="text-plum-deep/60">{c.tags} tags</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-cream overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${(c.tags / maxColour) * 100}%`, background: c.hex || "#EC2675" }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
